import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { exchangeSession, listOffers, deleteOffer } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/lib/useBrand";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Lightning, ArrowsClockwise, Trash, ArrowRight } from "@phosphor-icons/react";
import { Toaster, toast } from "sonner";
import NewOffer from "@/pages/NewOffer";
import OfferDetail from "@/pages/OfferDetail";

/**
 * EmbedShell — minimal-chrome wrapper to render OfferAlchemy inside the portal
 * iframe at therahulgangurde.com.
 *
 * Authentication: receives an HS256 portal JWT either as a URL hash fragment
 * (#token=...) OR a query string (?token=...). Exchanges it via the dedicated
 * embed exchange endpoint, then renders the embedded routes WITHOUT the global
 * Navbar / Footer (the portal owns the chrome).
 */

// Module-scope capture survives React StrictMode unmount/remount in dev.
// Without this, the first mount's replaceState clears the URL, then the second
// mount re-reads an empty URL and thinks no token was ever provided.
const captureInitialToken = () => {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash || "";
  const m = hash.match(/token=([^&]+)/);
  if (m) return decodeURIComponent(m[1]);
  const search = new URLSearchParams(window.location.search);
  return search.get("token") || "";
};
const _CAPTURED_TOKEN = captureInitialToken();
let _EXCHANGE_PROMISE = null; // de-dupes exchange across StrictMode double-runs

// Exchange portal JWT for an OfferAlchemy session
async function exchangePortalToken(token) {
  const { data } = await api.post("/auth/portal-session", { token });
  return data;
}

function EmbedHome() {
  const { user } = useAuth();
  const { name: appName } = useBrand();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    listOffers()
      .then(setOffers)
      .catch(() => toast.error("Could not load your offers"))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (id) => {
    try {
      await deleteOffer(id);
      setOffers((p) => p.filter((o) => o.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Delete failed");
    }
  };

  const totalCredits =
    (user?.credits ?? 0) + (user && !user.has_used_free_credit ? 1 : 0);
  const isAdmin = !!user?.is_admin;

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] p-6 md:p-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-2">
              Your portal bonus
            </p>
            <h1 className="font-display text-3xl md:text-4xl font-light tracking-tighter leading-none">
              Forge an <span className="font-black">Irresistible Offer</span>
            </h1>
            <p className="mt-2 text-sm text-[#9AA5B5]">
              See your product&apos;s scale-to-max path in under two minutes.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div
              className="px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-xs font-mono text-[#C9A961]"
              data-testid="embed-credits-badge"
            >
              <Lightning weight="fill" className="inline h-3.5 w-3.5 mr-1" />
              {isAdmin ? (
                <>
                  <span className="font-bold">∞</span>
                  <span className="text-[#9AA5B5] ml-1">unlimited</span>
                </>
              ) : (
                <>
                  <span className="font-bold">{totalCredits}</span>
                  <span className="text-[#9AA5B5] ml-1">credits</span>
                </>
              )}
            </div>
            <Button
              onClick={() => navigate("/embed/new")}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm"
              data-testid="embed-new-btn"
            >
              <Plus weight="bold" className="mr-2 h-4 w-4" />
              New offer
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-12 text-center text-[#9AA5B5]">
            Loading…
          </div>
        ) : offers.length === 0 ? (
          <div className="bg-[#0F1F38] border border-white/5 rounded-lg p-12 text-center" data-testid="embed-empty">
            <ArrowsClockwise weight="duotone" className="h-10 w-10 text-[#C9A961] mx-auto mb-4" />
            <h3 className="font-display text-xl font-light mb-2">No offers yet.</h3>
            <p className="text-sm text-[#9AA5B5] mb-6">
              Forge your first one — it&apos;s free.
            </p>
            <Button
              onClick={() => navigate("/embed/new")}
              className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm"
              data-testid="embed-empty-cta"
            >
              <Plus weight="bold" className="mr-2 h-4 w-4" />
              Forge my first offer
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            {offers.map((offer, i) => (
              <motion.div
                key={offer.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="col-span-12 md:col-span-6 bg-[#0F1F38] border border-white/5 hover:border-[#C9A961]/40 transition-all rounded-lg p-6 flex flex-col"
                data-testid={`embed-offer-${offer.id}`}
              >
                <p className="text-[10px] tracking-[0.25em] uppercase text-[#C9A961] font-bold mb-2">
                  {new Date(offer.created_at).toLocaleDateString()}
                </p>
                <h3 className="font-display font-bold text-base text-white leading-snug line-clamp-3 mb-2">
                  {offer.output?.headline || "Untitled"}
                </h3>
                <p className="text-xs text-[#9AA5B5] line-clamp-2 mb-4 flex-1">
                  {offer.input?.current_offer}
                </p>
                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <button
                    onClick={() => navigate(`/embed/offer/${offer.id}`)}
                    className="text-sm text-[#C9A961] hover:text-[#E0B64B] flex items-center gap-1"
                    data-testid={`embed-open-${offer.id}`}
                  >
                    Open <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(offer.id)}
                    className="text-[#808A9B] hover:text-[#ef4444] p-1"
                    aria-label="Delete"
                  >
                    <Trash className="h-3.5 w-3.5" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Compact legal disclaimer (still required) */}
        <p
          className="mt-10 text-[10px] leading-relaxed text-[#808A9B] max-w-3xl"
          data-testid="embed-legal-disclaimer"
        >
          <strong className="text-[#888]">Disclaimer:</strong> {appName} is an
          independent tool inspired by the &quot;$100M Offers&quot; framework. Not
          affiliated with, endorsed by, or associated with Alex Hormozi or
          Acquisition.com.
        </p>
      </div>
    </div>
  );
}

export default function EmbedShell() {
  const { user, loading, setUser } = useAuth();
  const { name: appName } = useBrand();
  const location = useLocation();
  const [phase, setPhase] = useState("init"); // init | exchanging | ready | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      // If we already have a user (cookie session or previous exchange), we're done.
      if (user) {
        setPhase("ready");
        return;
      }
      // Still loading existing session check — wait for that to settle.
      if (loading) {
        setPhase("init");
        return;
      }
      // No cookie session. Try the captured JWT.
      if (!_CAPTURED_TOKEN) {
        setErrorMsg("No portal session token provided");
        setPhase("error");
        return;
      }

      setPhase("exchanging");
      // De-dupe across StrictMode double-effect — share one in-flight promise.
      if (!_EXCHANGE_PROMISE) {
        _EXCHANGE_PROMISE = exchangePortalToken(_CAPTURED_TOKEN);
      }
      try {
        const u = await _EXCHANGE_PROMISE;
        if (cancelled) return;
        setUser(u);
        if (typeof window !== "undefined") {
          window.history.replaceState(null, "", location.pathname);
        }
        setPhase("ready");
      } catch (e) {
        console.error("embed sso failed", e);
        if (cancelled) return;
        setErrorMsg(e?.response?.data?.detail || "Portal SSO failed");
        setPhase("error");
        _EXCHANGE_PROMISE = null; // allow retry on next mount
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  if (phase === "init" || phase === "exchanging" || loading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center" data-testid="embed-loader">
        <div className="flex flex-col items-center gap-3">
          <div className="h-9 w-9 rounded-full border-2 border-[#C9A961] border-t-transparent animate-spin" />
          <p className="text-xs tracking-[0.3em] uppercase text-[#9AA5B5]">
            Securely opening your forge
          </p>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex items-center justify-center p-6" data-testid="embed-error">
        <div className="max-w-md text-center">
          <h2 className="font-display text-2xl mb-3">Couldn&apos;t open {appName}</h2>
          <p className="text-sm text-[#9AA5B5] mb-2">{errorMsg}</p>
          <p className="text-xs text-[#808A9B]">
            Refresh the portal page to re-issue your access token.
          </p>
        </div>
      </div>
    );
  }

  // Authed inside the iframe. Render embed-only routes.
  return (
    <>
      <Toaster theme="dark" position="top-right" />
      <Routes>
        <Route index element={<EmbedHome />} />
        <Route path="new" element={<NewOffer />} />
        <Route path="offer/:id" element={<OfferDetail />} />
        <Route path="*" element={<Navigate to="" replace />} />
      </Routes>
    </>
  );
}
